import {
  Stack,
  Typography,
  Box,
  Divider,
  Button,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  Tooltip,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { formatRelative } from 'date-fns';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import CloseIcon from '@mui/icons-material/Close';

type Props = {
  agentInstance: any;
  onClose?: () => void;
  onExecute?: () => void;
  isExecuting?: boolean;
  suppressEvents?: boolean;
  onToggleSuppressEvents?: (val: boolean) => void;
};

const AgentHeader = ({
  agentInstance = {},
  onClose,
  onExecute,
  isExecuting,
  suppressEvents,
  onToggleSuppressEvents,
}: Props) => {
  const { t } = useTranslation();

  return (
    <Stack
      direction={'row'}
      spacing={2}
      alignItems="center"
      justifyContent="space-between"
      className="glass-card-light"
      sx={{
        px: 1.5,
        py: 0.5,
        borderRadius: '12px',
        borderBottom: 'none',
        boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
      }}
    >
      <Stack direction="row" spacing={2} alignItems="center">
        <SmartToyIcon color="primary" sx={{ opacity: 0.8 }} />
        <Box>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 'bold', color: 'primary.main', fontSize: '1rem', lineHeight: 1.2 }}
          >
            {agentInstance?.name}
          </Typography>
          <Typography
            variant="caption"
            sx={{ opacity: 0.7, textTransform: 'uppercase', letterSpacing: 1, fontSize: '0.65rem' }}
          >
            {agentInstance?.agentDef?.name}
          </Typography>
        </Box>
      </Stack>

      <Stack direction="row" spacing={2} alignItems="center">
        <Stack
          direction="row"
          spacing={3}
          divider={<Divider orientation="vertical" flexItem sx={{ height: 16, my: 'auto' }} />}
          sx={{ display: { xs: 'none', md: 'flex' } }}
        >
          {agentInstance?.createdDate && (
            <Stack direction="row" spacing={1} alignItems="center">
              <AccessTimeIcon sx={{ fontSize: 14, opacity: 0.6 }} />
              <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                {t('common.created')}: {formatRelative(new Date(agentInstance.createdDate), new Date())}
              </Typography>
              <PersonOutlineIcon sx={{ fontSize: 14, opacity: 0.6 }} />
              <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                {agentInstance.createdBy}
              </Typography>
            </Stack>
          )}
          {agentInstance?.lastUpdatedDate && (
            <Stack direction="row" spacing={1} alignItems="center">
              <AccessTimeIcon sx={{ fontSize: 14, opacity: 0.6 }} />
              <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                {t('common.updated')}: {formatRelative(new Date(agentInstance.lastUpdatedDate), new Date())}
              </Typography>
              <PersonOutlineIcon sx={{ fontSize: 14, opacity: 0.6 }} />
              <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                {agentInstance.lastUpdatedBy}
              </Typography>
            </Stack>
          )}
        </Stack>

        {onExecute && onToggleSuppressEvents && (
          <Tooltip
            title={
              suppressEvents
                ? 'Run this step only: Executes this step without triggering subsequent steps in the pipeline.'
                : 'Run and continue: Executes this step and automatically triggers subsequent steps in the pipeline.'
            }
          >
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={suppressEvents}
                  onChange={(e) => onToggleSuppressEvents(e.target.checked)}
                  disabled={isExecuting}
                  sx={{
                    p: 0.5,
                    '&.Mui-checked': { color: 'primary.main' },
                    '& .MuiSvgIcon-root': { fontSize: 18 },
                  }}
                />
              }
              label={
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    color: suppressEvents ? 'primary.main' : 'text.secondary',
                    userSelect: 'none',
                    whiteSpace: 'nowrap',
                  }}
                >
                  Run this step only
                </Typography>
              }
              sx={{ mr: 0.5, ml: 0 }}
            />
          </Tooltip>
        )}

        {onExecute && (
          <Button
            size="small"
            variant="contained"
            color="primary"
            disabled={isExecuting}
            onClick={onExecute}
            startIcon={
              isExecuting ? (
                <CircularProgress size={14} color="inherit" />
              ) : (
                <PlayArrowIcon sx={{ fontSize: 16 }} />
              )
            }
            sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '8px', whiteSpace: 'nowrap' }}
          >
            {isExecuting ? 'Running Agent...' : 'Run Agent'}
          </Button>
        )}

        {onClose && (
          <Button
            size="small"
            variant="outlined"
            color="primary"
            onClick={onClose}
            startIcon={<CloseIcon />}
            sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '8px', whiteSpace: 'nowrap' }}
          >
            {t('common.buttons.close')}
          </Button>
        )}
      </Stack>
    </Stack>
  );
};

export default AgentHeader;
